import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dumbbell, Image as ImageIcon, Plus, Trash2, X, Loader2, Play, ChevronRight } from 'lucide-react';
import { compressImage } from '../utils/imageCompress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAdminAuth = () => {
  try { return JSON.parse(sessionStorage.getItem('gw_admin_auth') || 'null'); } catch { return null; }
};

// Fixed structure — the squares. Admin just taps a square and uploads; videos are set here.
export const SECTIONS = [
  {
    id: 'stretches',
    title: 'Stretches',
    blurb: 'Gentle, progressive stretches on the ball — tap a square to move through the series.',
    cards: [
      {
        id: 'back-spine-abs',
        title: 'Back / Spine / Abdominals',
        videos: [
          {
            title: 'Spinal Movements and the Ball',
            youtubeId: 'RQj9rbxuos4',
            blurb: 'An easy, accessible way to reach the deeper, subtle postural system — a lovely way to loosen up and move more freely. Explore Hula hoops, Figure 8s and Bouncing, both loose-and-free and more activated.',
          },
        ],
      },
      { id: 'chest-lats-triceps', title: 'Chest / Lats / Triceps' },
      { id: 'quad-lunge', title: 'Quad / Lunge' },
    ],
  },
  {
    id: 'strength',
    title: 'Pattern / Progressions',
    blurb: 'Build strength step by step — each square walks you through the pattern and progression.',
    cards: [
      { id: 'core-progressions', title: 'Core Pattern / Progressions' },
      { id: 'posture-spine-progressions', title: 'Posture / Spine Pattern / Progressions' },
      {
        id: 'hamstring-progressions',
        title: 'Bend Pattern / Progressions',
        videos: [
          { title: 'Hamstring Repatterning', youtubeId: 'CfkUF4ScixU' },
          { title: 'Bend Patterns — stick work', youtubeId: 'U3WaJWzlfQU' },
          { title: 'Walking out of a chair', youtubeId: 'RDFk17D6SoE' },
        ],
      },
      { id: 'squat-progressions', title: 'Squat Pattern / Progressions' },
    ],
  },
];

// Pattern/Progression videos surfaced into Jill's Corner as a tickable checklist category.
// (The Stability Ball intro video already syncs via the VIP library, so only the strength
// section is mirrored here to avoid duplicates.)
export const ballVideoChecklist = SECTIONS
  .filter((s) => s.id === 'strength')
  .map((section) => {
    const items = section.cards.flatMap((card) =>
      (card.videos || []).map((v, i) => ({
        id: `ballvid-${card.id}-${i}`,
        label: v.title,
        videoUrl: `https://youtu.be/${v.youtubeId}`,
      }))
    );
    return items.length
      ? { id: `ballvideos-${section.id}`, name: 'Pattern / Progressions', blurb: 'Tick the videos we explored together.', layout: 'list', items }
      : null;
  })
  .filter(Boolean);

const imgUrl = (id) => `${API}/vip/ball-images/${id}/file`;

export default function StabilityBallProgressions({ only }) {
  const admin = getAdminAuth();
  const [items, setItems] = useState({});
  const [activeCard, setActiveCard] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');

  const sections = SECTIONS.filter((s) => !only || s.id === only);

  const load = async () => {
    try {
      const res = await fetch(`${API}/vip/ball-images`);
      if (res.ok) setItems((await res.json()).items || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    document.body.style.overflow = activeCard ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [activeCard]);

  const cardImages = (cid) => items[cid] || [];

  const addImage = async (cardId, file) => {
    if (!admin || !file) return;
    setUploading(true);
    try {
      const toSend = await compressImage(file);
      const fd = new FormData();
      fd.append('file', toSend);
      fd.append('card_id', cardId);
      fd.append('caption', caption);
      const res = await fetch(
        `${API}/vip/ball-images?admin_email=${encodeURIComponent(admin.email)}&admin_password=${encodeURIComponent(admin.password)}`,
        { method: 'POST', body: fd }
      );
      if (res.ok) {
        setCaption('');
        await load();
      } else {
        alert(res.status === 413 ? 'That image is too large.' : 'Upload failed — please try again.');
      }
    } catch {
      alert('Upload failed — please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imgId) => {
    if (!admin) return;
    if (!window.confirm('Remove this picture?')) return;
    const res = await fetch(
      `${API}/vip/ball-images/${imgId}?admin_email=${encodeURIComponent(admin.email)}&admin_password=${encodeURIComponent(admin.password)}`,
      { method: 'DELETE' }
    );
    if (res.ok) await load();
  };

  const editCaption = async (imgId, current) => {
    if (!admin) return;
    const next = window.prompt('Edit caption', current || '');
    if (next === null) return;
    const fd = new FormData();
    fd.append('caption', next);
    const res = await fetch(
      `${API}/vip/ball-images/${imgId}?admin_email=${encodeURIComponent(admin.email)}&admin_password=${encodeURIComponent(admin.password)}`,
      { method: 'PUT', body: fd }
    );
    if (res.ok) await load();
  };

  const allCards = SECTIONS.flatMap((s) => s.cards);
  const activeCardMeta = activeCard ? allCards.find((c) => c.id === activeCard) : null;
  const activeImages = activeCard ? cardImages(activeCard) : [];
  const activeVideos = activeCardMeta?.videos || [];

  return (
    <div data-testid="stability-ball-progressions">
      {sections.map((section) => (
        <section key={section.id} className="mb-10" data-testid={`ball-section-${section.id}`}>
          <div className="rounded-2xl border border-stone-200 bg-[#e4efe8] p-6 md:p-7 mb-5">
            <h2 className="font-heading text-2xl md:text-3xl text-forest flex items-center gap-2">
              <Dumbbell size={22} className="text-[#5a7a6a]" /> {section.title}
            </h2>
            <p className="text-stone-600 font-body italic mt-1">{section.blurb}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {section.cards.map((card) => {
              const imgs = cardImages(card.id);
              const cover = imgs[0];
              const vids = card.videos || [];
              const hasVideo = vids.length > 0;
              const count = imgs.length;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setActiveCard(card.id)}
                  className="text-left bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  data-testid={`ball-card-${card.id}`}
                >
                  <div className="relative aspect-video bg-[#e4efe8] flex items-center justify-center overflow-hidden">
                    {cover ? (
                      <img src={imgUrl(cover.id)} alt={card.title} loading="lazy" className="w-full h-full object-cover" />
                    ) : hasVideo ? (
                      <img src={`https://img.youtube.com/vi/${vids[0].youtubeId}/hqdefault.jpg`} alt={card.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center text-[#5a7a6a] font-body">
                        <ImageIcon size={22} className="mx-auto mb-2 opacity-50" />
                        <p className="text-xs uppercase tracking-[0.2em]">Pictures coming soon</p>
                      </div>
                    )}
                    {hasVideo && (
                      <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-cream/95 text-forest text-[10px] font-body font-medium px-2 py-1 rounded-full shadow-sm">
                        <Play size={10} /> {vids.length} {vids.length === 1 ? 'video' : 'videos'}
                      </span>
                    )}
                    {count > 0 && (
                      <span className="absolute bottom-2 right-2 bg-forest/90 text-cream text-[10px] font-body px-2 py-1 rounded-full">
                        {count} {count === 1 ? 'step' : 'steps'}
                      </span>
                    )}
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <h3 className="font-heading text-base text-forest leading-snug">{card.title}</h3>
                    <ChevronRight size={16} className="text-stone-300 flex-shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {/* Progression modal */}
      <AnimatePresence>
        {activeCard && activeCardMeta && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-forest/80 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
            onClick={() => setActiveCard(null)}
            data-testid="ball-progression-modal"
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
              className="bg-cream rounded-2xl w-full max-w-2xl my-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-stone-200 sticky top-0 bg-cream rounded-t-2xl z-10">
                <h3 className="font-heading text-xl text-forest">{activeCardMeta.title}</h3>
                <button onClick={() => setActiveCard(null)} className="text-stone-400 hover:text-forest" data-testid="ball-modal-close">
                  <X size={22} />
                </button>
              </div>

              <div className="p-5 space-y-5">
                {activeVideos.map((v, vi) => (
                  <div key={v.youtubeId} data-testid={`ball-video-${activeCard}-${vi}`}>
                    <div className="aspect-video rounded-xl overflow-hidden bg-black mb-2">
                      <iframe
                        title={v.title}
                        src={`https://www.youtube.com/embed/${v.youtubeId}?rel=0&playsinline=1`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                    <p className="font-heading text-base text-forest">{v.title}</p>
                    {v.blurb && <p className="font-body text-sm text-stone-500 leading-relaxed">{v.blurb}</p>}
                  </div>
                ))}

                {activeImages.length === 0 && activeVideos.length === 0 && (
                  <p className="font-body text-stone-500 text-center py-6" data-testid="ball-empty">
                    {admin ? 'No pictures yet — add your first one below.' : 'Pictures coming soon.'}
                  </p>
                )}

                {activeImages.map((im, i) => (
                  <div key={im.id} className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid={`ball-step-${im.id}`}>
                    <div className="flex items-center justify-between px-4 pt-3">
                      <span className="font-body text-xs font-medium text-[#5a7a6a] uppercase tracking-wider">Step {i + 1}</span>
                      {admin && (
                        <div className="flex items-center gap-3">
                          <button onClick={() => editCaption(im.id, im.caption)} className="text-stone-400 hover:text-forest font-body text-xs underline" data-testid={`ball-edit-${im.id}`}>Edit caption</button>
                          <button onClick={() => deleteImage(im.id)} className="text-stone-300 hover:text-red-600" title="Remove" data-testid={`ball-delete-${im.id}`}><Trash2 size={15} /></button>
                        </div>
                      )}
                    </div>
                    <img src={imgUrl(im.id)} alt={im.caption || `Step ${i + 1}`} loading="lazy" className="w-full max-h-[70vh] object-contain bg-stone-50 mt-2" />
                    {im.caption && <p className="font-body text-sm text-stone-600 px-4 py-3">{im.caption}</p>}
                  </div>
                ))}

                {admin && (
                  <div className="bg-[#eef4f0] border border-[#5a7a6a]/20 rounded-xl p-4" data-testid="ball-admin-upload">
                    <p className="font-heading text-sm text-forest mb-2">Add a picture to this progression</p>
                    <input
                      type="text"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Short caption (e.g. 'Level 1 — wide stance')"
                      className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest mb-3"
                      data-testid="ball-caption-input"
                    />
                    <label className="inline-flex items-center gap-2 cursor-pointer text-sm font-body text-cream bg-forest px-4 py-2 rounded-full hover:bg-forest-dark transition-colors" data-testid="ball-upload-label">
                      {uploading ? (<><Loader2 size={15} className="animate-spin" /> Uploading…</>) : (<><Plus size={15} /> Choose picture</>)}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; addImage(activeCard, f); }}
                        data-testid="ball-file-input"
                      />
                    </label>
                    <p className="font-body text-[11px] text-stone-400 mt-2">Add them in order — each one becomes the next step. Type the caption first, then pick the picture. Photos shrink automatically.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
